import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, FileDown, Mail } from "lucide-react";
import { SectionHeading } from "@/components/Produzioni";
import { COMUNICATI, STAMPA } from "@/data/content";
import { useLang, pick } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function Stampa() {
    const { lang, ui } = useLang();
    const [openId, setOpenId] = useState(null);

    return (
        <section id="stampa" data-testid="stampa-section" className="relative border-t border-white/5 bg-[#0a0a0d] px-5 py-24 sm:px-8 sm:py-32">
            <div className="mx-auto max-w-7xl">
                <SectionHeading
                    label={ui.stampa.label}
                    title={ui.stampa.title}
                    sub={ui.stampa.sub}
                    testid="stampa-heading"
                />

                <div className="grid gap-14 lg:grid-cols-12">
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="divide-y divide-white/10 border-y border-white/10 lg:col-span-7"
                    >
                        {COMUNICATI.map((c) => (
                            <div key={c.id} data-testid={`comunicato-${c.id}`}>
                                <button
                                    data-testid={`comunicato-toggle-${c.id}`}
                                    onClick={() => setOpenId(openId === c.id ? null : c.id)}
                                    className="group flex w-full items-start justify-between gap-6 py-6 text-left"
                                >
                                    <div>
                                        <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em]">
                                            <span className="text-brand">{pick(lang, c, "tag")}</span>
                                            <span className="text-slate-500">{pick(lang, c, "data")}</span>
                                        </div>
                                        <h3 className="mt-2.5 font-display text-xl font-bold text-white transition-colors duration-300 group-hover:text-brand sm:text-2xl">
                                            {pick(lang, c, "titolo")}
                                        </h3>
                                        <p className="mt-2 text-sm text-slate-400">{pick(lang, c, "estratto")}</p>
                                    </div>
                                    <ChevronDown
                                        className={`mt-2 h-5 w-5 shrink-0 text-slate-500 transition-transform duration-300 ${
                                            openId === c.id ? "rotate-180 text-brand" : ""
                                        }`}
                                    />
                                </button>
                                <AnimatePresence initial={false}>
                                    {openId === c.id && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.45, ease: EASE }}
                                            className="overflow-hidden"
                                        >
                                            <p data-testid={`comunicato-testo-${c.id}`} className="pb-7 pr-4 text-sm leading-relaxed text-slate-300">
                                                {pick(lang, c, "testo")}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1, delay: 0.1, ease: EASE }}
                        className="space-y-6 lg:col-span-5"
                    >
                        <div className="rounded-3xl border border-white/10 bg-charcoal p-7">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
                                <FileDown className="h-5 w-5" />
                            </span>
                            <h3 className="mt-5 font-display text-xl font-bold text-white">{ui.stampa.kitTitle}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-400">{ui.stampa.kitText}</p>
                            <a
                                data-testid="press-kit-download-button"
                                href="/press-kit-red-private.pdf"
                                download
                                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark"
                            >
                                <FileDown className="h-4 w-4" />
                                {ui.stampa.kitBtn}
                            </a>
                        </div>

                        <div className="rounded-3xl border border-white/10 bg-charcoal p-7">
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
                                <Mail className="h-5 w-5" />
                            </span>
                            <h3 className="mt-5 font-display text-xl font-bold text-white">{ui.stampa.contactTitle}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-400">{ui.stampa.contactText}</p>
                            <a
                                data-testid="press-contact-button"
                                href={`mailto:${STAMPA.contatto}`}
                                className="mt-6 flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-brand hover:text-brand"
                            >
                                <Mail className="h-4 w-4" />
                                {STAMPA.contatto}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
